import type { ReactNode } from "react";
import useSWR from "swr";

import { api, type Me } from "../services/api";

/** Customers + onboarding: the backend list/create endpoints are iquest_admin only. */
export function isIquestAdmin(me: Me): boolean {
  return me.role === "iquest_admin";
}

/** KB admin: iQuest staff, or anyone holding the kb_author grant
 *  (mirrors _require_kb_admin in backend/app/routers/kb.py). */
export function canAuthorKb(me: Me): boolean {
  return (
    me.role === "iquest_admin" ||
    me.role === "service_desk" ||
    me.permissions.includes("kb_author")
  );
}

/**
 * Route-level access check for admin pages that do not check the caller
 * themselves. `/admin` has no role guard of its own (AdminLayout is a bare
 * Outlet) and the sidebar only hides links, so without this a client user who
 * opens one of these URLs mounted the page and fired its iquest_admin-only
 * requests, which 403'd behind a broken page (#236). The page is not mounted,
 * so nothing is fetched, until /api/me confirms access.
 */
export function AdminAccessGate({
  allow,
  blurb,
  children,
}: {
  allow: (me: Me) => boolean;
  blurb: string;
  children: ReactNode;
}) {
  const { data: me, error, mutate } = useSWR<Me>("/api/me", () => api.me());

  if (error && !me) {
    return (
      <div className="card empty-state">
        <p>We couldn't check your access. Check your connection, then try again.</p>
        <button type="button" className="btn btn-primary" onClick={() => void mutate()}>
          Try again
        </button>
      </div>
    );
  }
  if (!me) {
    return <div className="card">Loading…</div>;
  }
  if (!allow(me)) {
    return (
      <div className="card empty-state">
        <p>You don&apos;t have access to this page.</p>
        <p className="empty-state-blurb">{blurb}</p>
      </div>
    );
  }
  return <>{children}</>;
}
